import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { workspaceStore } from '~/config/workspaceStore';

/** Paths that require ?workspaceId= for scoped tenant data (see backend contract). */
function pathnameRequiresWorkspaceId(pathname: string): boolean {
  if (
    pathname.startsWith('/api/workspaces') ||
    pathname.startsWith('/api/users') ||
    pathname.startsWith('/api/subscription') ||
    pathname.startsWith('/api/notifications')
  ) {
    return false;
  }
  if (pathname === '/api/dashboard') return false;
  if (pathname.startsWith('/api/assets')) return true;
  if (pathname.startsWith('/api/deadline')) return true;
  if (pathname.startsWith('/api/drive')) return true;
  if (pathname.startsWith('/api/documents/analyze')) return true;
  if (pathname.startsWith('/api/mass-balance')) return true;
  if (pathname.startsWith('/api/home')) return true;

  return false;
}

/**
 * Configuration for DeadlineGuard service integration
 */
export const deadlineGuardConfig = {
  /**
   * Base URL for DeadlineGuard API
   * Should be set via VITE_DEADLINEGUARD_API_URL environment variable
   * Defaults to http://localhost:9090 for local development
   */
  apiUrl: import.meta.env.VITE_DEADLINEGUARD_API_URL || 'http://localhost:9090',

  /**
   * Get full URL for a specific endpoint
   * @param endpoint - API endpoint path (e.g., '/api/deadline')
   * @returns Full URL to the endpoint
   */
  getEndpointUrl: (endpoint: string): string => {
    const baseUrl = deadlineGuardConfig.apiUrl.replace(/\/$/, ''); // Remove trailing slash
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    return `${baseUrl}${path}`;
  },

  /**
   * Same as getEndpointUrl but appends workspaceId query param for workspace-scoped APIs
   * when a current workspace is selected.
   */
  getScopedEndpointUrl: (endpoint: string): string => {
    const base = deadlineGuardConfig.getEndpointUrl(endpoint);
    const url = new URL(base);
    if (!pathnameRequiresWorkspaceId(url.pathname)) {
      return base;
    }
    const wid = workspaceStore.getCurrentWorkspaceId();
    if (wid === null) {
      return base;
    }
    url.searchParams.set('workspaceId', String(wid));

    return url.toString();
  },

  /**
   * Get headers with Firebase Auth token for authenticated requests
   * @returns Promise with headers object containing Authorization header
   */
  getAuthHeaders: async (): Promise<Record<string, string>> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    try {
      const currentUser = firebase.auth().currentUser;
      if (currentUser) {
        const token = await currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get Firebase Auth token:', error);
    }

    return headers;
  },

  /**
   * Get or create user in DeadlineGuard service
   * @param timezone - Optional timezone for user creation
   * @param language - Optional language code for user creation
   * @returns Promise with UserDTO or null if failed
   */
  syncUser: async (timezone?: string, language?: string): Promise<unknown | null> => {
    try {
      const headers = await deadlineGuardConfig.getAuthHeaders();
      const url = deadlineGuardConfig.getEndpointUrl('/api/users/me');

      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      try {
        // Try to get current user (this will create user if doesn't exist)
        const response = await fetch(url, {
          method: 'GET',
          headers,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          const userData = await response.json();

          return userData;
        }

        // If user doesn't exist (404), create it
        if (response.status === 404) {
          const body: Record<string, string> = {};
          if (timezone) {
            body.timezone = timezone;
          }
          if (language) {
            body.language = language;
          }

          const createController = new AbortController();
          const createTimeoutId = setTimeout(() => createController.abort(), 10000);

          try {
            const createResponse = await fetch(deadlineGuardConfig.getEndpointUrl('/api/users'), {
              method: 'POST',
              headers,
              body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
              signal: createController.signal,
            });

            clearTimeout(createTimeoutId);

            if (createResponse.ok) {
              const userData = await createResponse.json();

              return userData;
            }
          } catch (createError) {
            clearTimeout(createTimeoutId);
            if (createError instanceof Error && createError.name === 'AbortError') {
              // eslint-disable-next-line no-console
              console.warn('User creation request timed out');
            }
            throw createError;
          }
        }

        // eslint-disable-next-line no-console
        console.error('Failed to sync user with DeadlineGuard service:', response.status);

        return null;
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          // eslint-disable-next-line no-console
          console.warn('User sync request timed out');

          return null;
        }
        throw fetchError;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error syncing user with DeadlineGuard service:', error);

      return null;
    }
  },
};
